import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "@fluentui/react/lib/Spinner";
import { Icon } from "@fluentui/react/lib/Icon";
import { TooltipHost } from "@fluentui/react";

import { AssociationListing, FolderListing } from "../index";

import { TEXT } from "../../../../../shared/constants/texts";
import { VIEW_FOLDER_AND_DOCUMENTS } from "../../../../../shared/constants/addToICIToggleView";
import { PAGE, PAGE_SIZE } from "../../../../../shared/constants/paginationConstants";
import { ICON } from "../../../../../shared/constants/icons";
import {
  UPLOAD_DRAG_AND_DROP_SIZE_LIMIT,
  UPLOAD_TYPE,
  UPLOAD_REQUEST_FROM,
  UPLOAD_FLOW,
} from "../../../../../shared/constants/uploadParams";
import { ROUTE } from "../../../../../shared/constants/routes";
import { logError } from "../../../../../shared/services/errorLogging";
import { callToUploadApi } from "../../../utils";
import {
  associationPartOneApiCall,
  associationPartTwoApiCall,
  bactInstanceApiCall,
  folderApiCall,
  instanceApiCall,
} from "../../../redux/apiCalls";
import {
  startFetchingAssociations,
  startUploadingDndAgreement,
  startFetchingFolders,
  startFetchingFolderInstances,
  hideNotification,
  showNotificationWarning,
  stopUploadingDndAgreement,
} from "../../../redux/slice";
import {
  setSelectedAgreementData,
  transformFolderData,
  updateFolderData,
  updateFolderInstanceData,
  transformAssociationPartOneData,
  transformAssociationPartTwoData,
  updateAssociationData,
  setSelectedDefaultAssociationData,
} from "../../../utilFunctions";

type AgreementListingProps = {
  agreements: any;
  getMoreAgreementData: any;
  hasMoreAgreements: any;
};

const AgreementListing: React.FC<AgreementListingProps> = (props) => {
  const { agreements, getMoreAgreementData, hasMoreAgreements } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.addToIciSlice);
  const {
    singleEmailData,
    areAttachmentsIncluded,
    isFetchingAgreementData,
    selectedAgreement,
    isUploadingDndAgreement,
    associationData,
    folderData,
    selectedViewFolderAndDocuments,
    defaultAssociationOfAgreement,
    tooltipAttributes,
    isAdvancePageEnabled,
  } = state;
  const [dndAgreementFiles, setDndAgreementFiles] = React.useState([]);
  const [showAssociationId, setShowAssociationId] = React.useState(null);
  const [showFolderId, setShowFolderId] = React.useState(null);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    if (selectedAgreement) {
      getAssociationsAndFolders();
    } else {
      setDndAgreementFiles([]);
      setShowAssociationId(null);
      setShowFolderId(null);
    }
  }, [selectedAgreement]);

  React.useEffect(() => {
    if (dndAgreementFiles && dndAgreementFiles.length > 0) {
      setShowAssociationId(null);
      setShowFolderId(null);
    }
  }, [dndAgreementFiles]);

  React.useEffect(() => {
    if (
      dndAgreementFiles.length > 0 &&
      selectedAgreement &&
      associationData &&
      associationData.length > 0 &&
      folderData
    ) {
      localStorage.setItem("selectedAgreement", JSON.stringify(selectedAgreement));
      localStorage.setItem("associationData", JSON.stringify(associationData));
      const areAttachmentsPresent =
        singleEmailData && singleEmailData.attachments && singleEmailData.attachments.length > 0;
      let defaultAssociation = null;
      if (folderData && folderData.defaultAssociationContractTypeName) {
        defaultAssociation = associationData.find(
          (item) => item.Name === folderData.defaultAssociationContractTypeName
        );
      }
      if (defaultAssociation) {
        setSelectedDefaultAssociationData(defaultAssociation);
      }
      if (dndAgreementFiles.length === 1 && areAttachmentsIncluded && areAttachmentsPresent) {
        history.push({
          pathname: ROUTE.SINGLE_EMAIL_UPLOAD,
          state: { from: UPLOAD_REQUEST_FROM.AGREEMENT, defaultAssociation: defaultAssociation },
        });
      } else {
        if (folderData === TEXT.NO_FOLDER_STRUCTURE_CONFIGURED) {
          if (dndAgreementFiles.length === 1) {
            history.push({
              pathname: ROUTE.SINGLE_EMAIL_UPLOAD,
              state: { from: UPLOAD_REQUEST_FROM.AGREEMENT, defaultAssociation: defaultAssociation },
            });
          } else {
            dispatch(showNotificationWarning(TEXT.NO_DEFAULT_ASSOCIATION));
            setTimeout(() => {
              dispatch(hideNotification());
            }, 5000);
            setDndAgreementFiles([]);
            dispatch(stopUploadingDndAgreement());
          }
        } else {
          callToUploadApiHandler({ defaultAssociation });
        }
      }
    }
  }, [selectedAgreement, associationData, folderData, dndAgreementFiles]);

  React.useEffect(() => {
    if (defaultAssociationOfAgreement) {
      getDefaultAssociationInstances();
    }
  }, [defaultAssociationOfAgreement]);

  React.useEffect(() => {
    if (
      associationData &&
      associationData.length > 0 &&
      folderData &&
      selectedViewFolderAndDocuments &&
      selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id
    ) {
      const defaultAssociation = associationData.find(
        (item) => item.Name === folderData.defaultAssociationContractTypeName
      );
      if (defaultAssociation) {
        setSelectedDefaultAssociationData(defaultAssociation);
      }
    }
  }, [associationData, folderData]);

  const callToUploadApiHandler = async ({ defaultAssociation }) => {
    let toBeUploaded = true;
    let base64Files = [];
    let uploadSize = 0;
    for (let file of dndAgreementFiles) {
      uploadSize = uploadSize + file.size;
      console.log("drag and drop upload size:", uploadSize);
      if (uploadSize >= UPLOAD_DRAG_AND_DROP_SIZE_LIMIT) {
        toBeUploaded = false;
        setDndAgreementFiles([]);
        dispatch(stopUploadingDndAgreement());
        dispatch(
          showNotificationWarning(TEXT.UPLOAD_SIZE_WARNING + UPLOAD_DRAG_AND_DROP_SIZE_LIMIT / 1000000 + TEXT.MB)
        );
        setTimeout(() => {
          dispatch(hideNotification());
        }, 5000);
        return;
      }
      const fileData = new Blob([file]);
      const b64 = await blobToBase64(fileData);
      base64Files.push({ name: file.name, b64: b64 });
    }
    if (toBeUploaded) {
      callToUploadApi({
        ...apiCallParams,
        state: state,
        dnd: true,
        type: UPLOAD_TYPE.NEW_DOCUMENT,
        from: UPLOAD_REQUEST_FROM.AGREEMENT,
        flow: UPLOAD_FLOW.DRAG_AND_DROP,
        uploadData: { defaultAssociation: defaultAssociation },
        files: base64Files,
      });
    }
    setDndAgreementFiles([]);
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = function () {
        resolve(reader.result);
      };
    });
  };

  const getAssociationsAndFolders = async () => {
    await getAssociations();
    await getFolders();
  };

  const getAssociations = async () => {
    dispatch(startFetchingAssociations());
    const { sysId, entityName } = selectedAgreement;
    const requestDataPartOne = { sysId: sysId, entityName: entityName };
    const associationPartOneResponse = await associationPartOneApiCall({
      ...apiCallParams,
      requestData: requestDataPartOne,
      retryFunctionCall: () => getAssociations(),
    });
    const { response: responsePartOne, error: errorPartOne } = associationPartOneResponse;
    if (responsePartOne) {
      const associationIds = transformAssociationPartOneData(responsePartOne);
      const requestDataPartTwo = { ids: associationIds };
      const associationPartTwoResponse = await associationPartTwoApiCall({
        ...apiCallParams,
        requestData: requestDataPartTwo,
        retryFunctionCall: () => getAssociations(),
      });
      const { response: responsePartTwo, error: errorPartTwo } = associationPartTwoResponse;
      if (responsePartTwo) {
        const associations = transformAssociationPartTwoData({ responsePartOne, responsePartTwo });
        updateAssociationData(associations);
      }
      if (errorPartTwo) {
        updateAssociationData([]);
        logError({
          dispatch: dispatch,
          err: errorPartTwo,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getAssociations",
            callerApi: "api/Instance/FindProjections",
          },
        });
      }
    }
    if (errorPartOne) {
      updateAssociationData([]);
      logError({
        dispatch: dispatch,
        err: errorPartOne,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getAssociations",
          callerApi:
            "Agreement/GetAssociationsWithEvaluatedDynamicAssociation?agreementId=&contractType=&evaluateDynamicAssociations=true&skipAmendment=true&skipPeer=true&skipMaster=true",
        },
      });
    }
  };

  const getFolders = async () => {
    dispatch(startFetchingFolders());
    const { entityName } = selectedAgreement;
    const requestData = { entityName: entityName };
    const folderResponse = await folderApiCall({
      ...apiCallParams,
      requestData: requestData,
      retryFunctionCall: () => getFolders(),
    });
    const { response, error } = folderResponse;
    if (response) {
      const folderData = transformFolderData(response);
      const { transformedFolderData, finalFolderData } = folderData;
      if (!transformedFolderData || !finalFolderData) {
        updateFolderData(TEXT.NO_FOLDER_STRUCTURE_CONFIGURED);
      }
      if (finalFolderData) {
        updateFolderData(finalFolderData);
      }
    }
    if (error) {
      updateFolderData(null);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getFolders",
          callerApi: "api/instance/FindProjectionsKeyValuePair",
        },
      });
    }
  };

  const getDefaultAssociationInstances = async () => {
    dispatch(startFetchingFolderInstances());
    if (defaultAssociationOfAgreement.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: defaultAssociationOfAgreement.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_DND,
        errMsg: errorValidationMsg.ERROR_LOADING_FOLDER_INSTANCES,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getDefaultAssociationInstances(),
      });
      const { response, error } = instanceResponse;
      if (response) {
        updateFolderInstanceData(response);
      }
      if (error) {
        updateFolderInstanceData([]);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getDefaultAssociationInstances",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: defaultAssociationOfAgreement.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_DND,
        errMsg: errorValidationMsg.ERROR_LOADING_FOLDER_INSTANCES,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getDefaultAssociationInstances(),
      });
      const { response, error } = instanceResponse;
      if (response) {
        updateFolderInstanceData(response);
      }
      if (error) {
        updateFolderInstanceData([]);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getDefaultAssociationInstances",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const onIconToggleClicked = (data) => {
    if (selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[0].id) {
      if (showAssociationId === data.sysId) {
        setSelectedAgreementData(null);
      } else {
        setShowAssociationId(data.sysId);
        setSelectedAgreementData(data);
      }
    }
    if (selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id) {
      if (showFolderId === data.sysId) {
        setSelectedAgreementData(null);
      } else {
        setShowFolderId(data.sysId);
        setSelectedAgreementData(data);
      }
    }
  };

  const handleAgreementDnD = (e, data) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(startUploadingDndAgreement());
    const files = e.dataTransfer.files;
    let uploadSize = 0;
    for (let file of files) {
      uploadSize = uploadSize + file.size;
    }
    setDndAgreementFiles(files);
    if (!selectedAgreement || (selectedAgreement && selectedAgreement.sysId !== data.sysId)) {
      setSelectedAgreementData(data);
    }
  };

  const handleDnD = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onEnterPressAgreement = (e, agreement) => {
    if (e.key === TEXT.ENTER || e.keyCode === TEXT.ENTER_KEY_CODE) {
      onIconToggleClicked(agreement);
    }
  };

  const goToAdvancePageHandler = (agreement) => {
    setSelectedAgreementData(agreement);
    history.push({
      pathname: ROUTE.SINGLE_EMAIL_UPLOAD,
      state: { from: null, defaultAssociation: null },
    });
  };

  let classNameForAgreementListing = "agreement-listing-container-add-to-ici";
  if (isAdvancePageEnabled) {
    classNameForAgreementListing =
      "agreement-listing-container-add-to-ici agreement-listing-container-with-banner-add-to-ici";
  }

  return (
    <div id="id-agreement-listing-add-to-ici" className={classNameForAgreementListing}>
      {isFetchingAgreementData ? (
        <LoadingAgreements />
      ) : agreements && agreements.length > 0 ? (
        <InfiniteScroll
          dataLength={agreements.length}
          next={getMoreAgreementData}
          hasMore={hasMoreAgreements}
          loader={
            <div className="agreement-loader-container-is-add-to-ici">
              <Spinner label={TEXT.LOADING_AGREEMENTS} labelPosition="right" />
            </div>
          }
          endMessage={
            <div className="agreement-end-message-container-is-add-to-ici">
              <p>{TEXT.END_OF_THE_AGREEMENT_LIST}</p>
            </div>
          }
          scrollableTarget="id-agreement-listing-add-to-ici"
        >
          {agreements.map((agreement) => {
            let classNameForSingleAgreement = "single-agreement-container-add-to-ici";
            if (!isUploadingDndAgreement && selectedAgreement && selectedAgreement.sysId === agreement.sysId) {
              classNameForSingleAgreement = "single-agreement-container-selected-add-to-ici";
            }
            const iconName =
              showAssociationId === agreement.sysId || showFolderId === agreement.sysId ? ICON.UP : ICON.DOWN;
            return (
              <React.Fragment key={agreement.sysId}>
                <div
                  className={classNameForSingleAgreement}
                  onDrop={(e) => handleAgreementDnD(e, agreement)}
                  onDragOver={(e) => handleDnD(e)}
                  onDragEnter={(e) => handleDnD(e)}
                  onDragLeave={(e) => handleDnD(e)}
                >
                  {!(selectedAgreement && isUploadingDndAgreement && selectedAgreement.sysId === agreement.sysId) ? (
                    <React.Fragment>
                      <div
                        className="icon-toggle"
                        onClick={() => onIconToggleClicked(agreement)}
                        tabIndex={1}
                        onKeyDown={(e) => onEnterPressAgreement(e, agreement)}
                        title={iconName}
                      >
                        <Icon iconName={iconName} />
                        <span className="sr-only">{iconName}</span>
                      </div>
                      <div className="agreement-label">
                        <TooltipHost
                          tooltipProps={{
                            onRenderContent: () => (
                              <React.Fragment>
                                {tooltipAttributes && tooltipAttributes.length > 0 ? (
                                  <div className="agreement-tooltip-container-add-to-ici">
                                    {tooltipAttributes.map((attr, index) => {
                                      for (let key in agreement) {
                                        if (attr.Key === key.toLowerCase()) {
                                          return (
                                            <p key={index}>
                                              <span>{attr.Value}</span>: {agreement[key]}
                                            </p>
                                          );
                                        }
                                      }
                                    })}
                                  </div>
                                ) : (
                                  <div className="agreement-tooltip-container-add-to-ici">
                                    <p>
                                      <span>{TEXT.NAME}</span>: {agreement.name}
                                    </p>
                                    <p>
                                      <span>{TEXT.CREATED_BY}</span>: {agreement.createdBy}
                                    </p>
                                    <p>
                                      <span>{TEXT.AGREEMENT_CODE}</span>: {agreement.iCMAgreementCode}
                                    </p>
                                    <p>
                                      <span>{TEXT.STATUS}</span>: {agreement.status}
                                    </p>
                                  </div>
                                )}
                              </React.Fragment>
                            ),
                          }}
                          id="agreement-tooltip-add-to-ici"
                        >
                          <p>{agreement.name ? agreement.name : agreement.iCMAgreementCode}</p>
                        </TooltipHost>
                      </div>
                      {isAdvancePageEnabled ? (
                        <div
                          className="icon-advance-page"
                          onClick={() => goToAdvancePageHandler(agreement)}
                          tabIndex={1}
                          title={TEXT.GO_TO_ADVANCE_PAGE}
                        >
                          <Icon iconName={ICON.ADVANCE_PAGE} />
                          <span className="sr-only">{ICON.ADVANCE_PAGE}</span>
                        </div>
                      ) : null}
                    </React.Fragment>
                  ) : (
                    <UploadToIci />
                  )}
                </div>
                {selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[0].id &&
                showAssociationId === agreement.sysId ? (
                  <AssociationListing />
                ) : null}
                {selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id &&
                showFolderId === agreement.sysId ? (
                  <FolderListing />
                ) : null}
              </React.Fragment>
            );
          })}
        </InfiniteScroll>
      ) : (
        <EmptyAgreements />
      )}
    </div>
  );
};

const LoadingAgreements = () => {
  return (
    <div className="agreement-listing-loading-container-add-to-ici">
      <Spinner size={3} label={TEXT.LOADING_AGREEMENTS} />
    </div>
  );
};

const EmptyAgreements = () => {
  return (
    <div className="agreement-listing-empty-container-add-to-ici">
      <div className="icon">
        <Icon iconName="TextDocument" />
        <span className="sr-only">{TEXT.TEXTDOCUMENT}</span>
      </div>
      <p>{TEXT.NO_AGREEMENTS_FOUND}</p>
    </div>
  );
};

const UploadToIci = () => {
  return (
    <div className="uploading-to-ici">
      <Spinner label={TEXT.UPLOADING_TO_ICI} labelPosition="right" />
    </div>
  );
};

export default AgreementListing;
